import {
  AloftArea,
  AloftDate,
  LatitudeLongitude,
  ManualAdjustments,
  addressToString,
  formatShortenedPropertyType,
  getAbbreviatedCarportGarage,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../redux/types.ts';
import { SalesComparisonAdjustmentKey, SalesComparisonGridColumnData } from '../types.ts';
import {
  formatFeatureArea,
  formatFeatureAreas,
  getDistanceToSubject,
  getExplicitZeroAdjustments,
  getFoundationDisplay,
  getPropertyImageWithFallback,
} from './utils.ts';

export const convertSalesComparisonDataToPDFGridColumn = async (
  id: string,
  compSequence: string[],
  data?: CompPropertyDataState['compData'] | SubjectPropertyDataState['subjectData'],
  adjustments?: CompAdjustments,
  manualAdjustments?: ManualAdjustments & { id?: string },
  subjectCoords?: LatitudeLongitude,
  entityListingPhotos?: Blob[],
  effectiveYear?: number,
): Promise<SalesComparisonGridColumnData | null> => {
  if (!data) return null;

  const {
    editableData: { propertyIdentification, transaction, site },
    readonlyData: { backupListingData },
  } = data;

  let { improvements } = data.editableData;

  if (id === 'subjectArv') {
    improvements = (data as SubjectPropertyDataState['subjectData']).editableData.arv!.improvements;
  }

  const address = orUndefined(addressToString)(propertyIdentification?.address) ?? '';

  let propertyImage: Blob | string | undefined = entityListingPhotos?.[0];

  if (!propertyImage) {
    propertyImage = await getPropertyImageWithFallback(
      backupListingData?.listing.Media?.[0]?.MediaURL,
      propertyIdentification?.coordinates,
    );
  }

  const distanceToSubject = getDistanceToSubject(
    subjectCoords,
    propertyIdentification?.coordinates,
  );

  const yearBuilt =
    effectiveYear && improvements.age !== undefined ? (effectiveYear - improvements.age).toString() : undefined;

  const aboveGradeHalfBaths = improvements.halfBaths || 0;
  const belowGradeHalfBaths =
    improvements.basement?.hasFeature && improvements.basement.halfBaths
      ? improvements.basement.halfBaths
      : 0;

  const aboveGradeFullBaths = improvements.fullBaths || 0;
  const belowGradeFullBaths =
    improvements.basement?.hasFeature && improvements.basement.fullBaths
      ? improvements.basement.fullBaths
      : 0;

  const explicitZeroAdjustments: Partial<Record<SalesComparisonAdjustmentKey, true>> | undefined =
    getExplicitZeroAdjustments(manualAdjustments);

  return {
    imageUrl: propertyImage ?? '',
    address,
    dataSource:
      typeof improvements?.featureGridDataSources === 'string'
        ? improvements.featureGridDataSources
        : '',
    proximity: distanceToSubject ? `${distanceToSubject} miles` : '',
    proximityMiles: distanceToSubject,
    listingStatus: transaction.status,
    salesPrice:
      transaction.status === 'SettledSale'
        ? orUndefined(schAloftPrice.parse)(transaction.price)
        : undefined,
    listingPrice: orUndefined(schAloftPrice.parse)(
      transaction.status === 'SettledSale' ? transaction.originalListPrice : transaction.price,
    ),
    dom: (transaction.daysOnMarket ?? '').toString(),
    contractDate: transaction.contractDate
      ? AloftDate.fromIsoString(`${transaction.contractDate}-01`).format('MM/yy')
      : '',
    saleDate: transaction.saleDate
      ? AloftDate.fromIsoString(`${transaction.saleDate}-01`).format('MM/yy')
      : '',
    siteSize: site.area ? AloftArea.schema.parse(site.area).toString(true) : '',
    siteInfluence: site?.influence ?? '',
    view: site?.view ?? '',
    quality: `Q${improvements.uadQuality ?? '-'}${getFoundationDisplay(improvements)}`,
    condition: `C${improvements.uadCondition ?? '-'}`,
    gla: improvements.gla
      ? AloftArea.fromNumber(improvements.gla.unitAmount, improvements.gla.unit).toString(true)
      : '',
    bedCount: improvements.bedrooms ? improvements.bedrooms.toString() : '',
    fullBaths: aboveGradeFullBaths ? aboveGradeFullBaths.toString() : '',
    halfBaths: aboveGradeHalfBaths ? aboveGradeHalfBaths.toString() : '',
    bathAboveGrade: `${aboveGradeFullBaths}.${aboveGradeHalfBaths}`,
    basementBaths: `${belowGradeFullBaths}.${belowGradeHalfBaths}`,
    additionalFeatures: {
      basementTotal: formatFeatureArea({
        area: improvements.basement?.totalArea?.unitAmount,
        unit: improvements.basement?.totalArea?.unit,
        hasFeature: improvements.basement?.hasFeature,
      }),
      basementFinished: formatFeatureArea({
        area: improvements.basement?.finishedArea?.unitAmount,
        unit: improvements.basement?.finishedArea?.unit,
        hasFeature: improvements.basement?.hasFeature,
      }),
      basementArea: [
        formatFeatureArea({
          area: improvements.basement?.totalArea?.unitAmount,
          unit: improvements.basement?.totalArea?.unit,
          hasFeature: improvements.basement?.hasFeature,
        }),
        formatFeatureArea({
          area: improvements.basement?.finishedArea?.unitAmount,
          unit: improvements.basement?.finishedArea?.unit,
          hasFeature: improvements.basement?.hasFeature,
        }),
      ]
        .filter(Boolean)
        .join('/'),
      basementFullBaths:
        belowGradeFullBaths && improvements.basement?.hasFeature
          ? belowGradeFullBaths.toString()
          : '',
      basementHalfBaths:
        belowGradeHalfBaths && improvements.basement?.hasFeature
          ? belowGradeHalfBaths.toString()
          : '',
      garage: getAbbreviatedCarportGarage(improvements) ?? '',
      porch: formatFeatureAreas([
        {
          area: improvements.porch?.area?.unitAmount,
          unit: improvements.porch?.area?.unit,
          type: 'Porch',
          hasFeature: improvements.porch?.hasFeature,
        },
        {
          area: improvements.patio?.area?.unitAmount,
          unit: improvements.patio?.area?.unit,
          type: 'Patio',
          hasFeature: improvements.patio?.hasFeature,
        },
        {
          area: improvements.deck?.area?.unitAmount,
          unit: improvements.deck?.area?.unit,
          type: 'Deck',
          hasFeature: improvements.deck?.hasFeature,
        },
      ]),
      attic: formatFeatureArea({
        area: improvements.attic?.area?.unitAmount,
        unit: improvements.attic?.area?.unit,
        hasFeature: improvements.attic?.hasFeature,
      }),
      fireplace:
        improvements.fireplace?.count && improvements.fireplace?.hasFeature
          ? `${improvements.fireplace.count} ${improvements.fireplace.type ? improvements.fireplace.type : ''}`.trim()
          : '',
      pool: formatFeatureAreas([
        {
          area: improvements.pool?.area?.unitAmount,
          unit: improvements.pool?.area?.unit,
          type: 'Pool',
          hasFeature: improvements.pool?.hasFeature,
        },
        {
          area: improvements.spa?.area?.unitAmount,
          unit: improvements.spa?.area?.unit,
          type: 'Spa',
          hasFeature: improvements.spa?.hasFeature,
        },
      ]),
      shed: formatFeatureArea({
        area: improvements.shed?.area?.unitAmount,
        unit: improvements.shed?.area?.unit,
        hasFeature: improvements.shed?.hasFeature,
      }),
      barn: formatFeatureArea({
        area: improvements.barn?.area?.unitAmount,
        unit: improvements.barn?.area?.unit,
        hasFeature: improvements.barn?.hasFeature,
      }),
      adu: formatFeatureArea({
        area: improvements.adu?.area?.unitAmount,
        unit: improvements.adu?.area?.unit,
        hasFeature: improvements.adu?.hasFeature,
      }),
      workshop: formatFeatureArea({
        area: improvements.workshop?.area?.unitAmount,
        unit: improvements.workshop?.area?.unit,
        hasFeature: improvements.workshop?.hasFeature,
      }),
    },
    totalCostToCureDisplayName: improvements?.totalCostToCureDisplayName ?? 'None',
    otherAdjustmentsDisplayName: improvements?.otherAdjustmentsDisplayName ?? 'Other',
    netAdj: adjustments?.netAdjustmentPct?.value ?? '',
    grossAdj: adjustments?.grossAdjustmentPct?.value ?? '',
    adjSalesPrice: adjustments?.adjustedSalePrice?.adjustment,
    adjustments: {
      saleDateAdjustment: adjustments?.dateOfSale?.adjustment,
      siteSizeAdjustment: adjustments?.site?.size?.adjustment,
      siteInfluenceAdjustment: adjustments?.siteInfluence?.adjustment,
      siteViewAdjustment: adjustments?.siteView?.adjustment,
      qualityAdjustment: adjustments?.quality?.adjustment,
      conditionAdjustment: adjustments?.condition?.adjustment,
      glaAdjustment: adjustments?.mainDwelling?.gla?.adjustment,
      bedCountAdjustment: adjustments?.bedrooms?.adjustment,
      fullBathsAdjustment: adjustments?.fullBaths?.adjustment,
      halfBathsAdjustment: adjustments?.halfBaths?.adjustment,
      bathAboveGradeAdjustment: adjustments?.bathAboveGrade?.adjustment,
      basementBathsAdjustment: adjustments?.bathBelowGrade?.adjustment,
      basementTotalAdjustment: adjustments?.basement?.adjustment,
      basementFinishedAdjustment: adjustments?.basement?.adjustment,
      basementAreaAdjustment: adjustments?.basement?.adjustment,
      basementHalfBathsAdjustment: adjustments?.basementHalfBaths?.adjustment,
      basementFullBathsAdjustment: adjustments?.basementFullBaths?.adjustment,
      garageAdjustment: adjustments?.garageCarport?.adjustment,
      porchAdjustment: adjustments?.porchPatioDeck?.adjustment,
      atticAdjustment: adjustments?.attic?.adjustment,
      fireplaceAdjustment: adjustments?.fireplace?.adjustment,
      poolAdjustment: adjustments?.pool?.adjustment,
      shedAdjustment: adjustments?.shed?.adjustment,
      barnAdjustment: adjustments?.barn?.adjustment,
      aduAdjustment: adjustments?.adu?.adjustment,
      workshopAdjustment: adjustments?.workshop?.adjustment,
      totalCostToCureAdjustment: adjustments?.totalCostToCure?.adjustment,
      otherAdjustments: adjustments?.other?.adjustment,
      netAdjPrice: adjustments?.netAdjustment?.adjustment,
    },
    explicitZeroAdjustments,
    compType: id === 'subject' ? undefined : (data as CompPropertyDataState['compData'])?.compType,
    position: compSequence.indexOf(id) + 1,
    yearBuilt,
    propertyType: formatShortenedPropertyType(improvements.propertyType) ?? '',
  };
};
